/**
 * OrbAvatar — WebGL animated gradient orb for the AI agent avatar.
 *
 * Faithfully ports all three visual layers from orb-avatar-generator.html:
 *   1. Radial halo glow behind the orb (colored by the warm palette anchor)
 *   2. Spinning orbit ring with a single tick mark (90s revolution)
 *   3. WebGL simplex-noise gradient canvas (continuous requestAnimationFrame loop)
 *
 * The default seed is the agent's fixed visual identity — every instance at the
 * same seed is visually identical. The `showRing` prop (default true) toggles
 * the orbit ring; parent containers with overflow-hidden naturally clip it.
 *
 * Dependencies: none (self-contained WebGL renderer + inline keyframe injection)
 */
import React, { useEffect, useMemo, useRef } from 'react';

/** Fixed visual identity seed for the agent — never changes. */
const AGENT_SEED = 0x4a5b6c7d;

// ── RNG ───────────────────────────────────────────────────────────────────────

function mulberry32(a: number): () => number {
    return function () {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// ── Palette ───────────────────────────────────────────────────────────────────

type RGB = [number, number, number];

function hslToRgb(h: number, s: number, l: number): RGB {
    h = ((h % 360) + 360) % 360;
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0,
        g = 0,
        b = 0;

    if (h < 60) {
        r = c;
        g = x;
    } else if (h < 120) {
        r = x;
        g = c;
    } else if (h < 180) {
        g = c;
        b = x;
    } else if (h < 240) {
        g = x;
        b = c;
    } else if (h < 300) {
        r = x;
        b = c;
    } else {
        r = c;
        b = x;
    }

    return [r + m, g + m, b + m];
}

function makePalette(rng: () => number): [RGB, RGB, RGB] {
    const hueA = rng() * 360;
    const spread = rng() < 0.75 ? 20 + rng() * 45 : 70 + rng() * 50;
    const hueB = hueA + (rng() < 0.5 ? spread : -spread);
    const cool = hslToRgb(hueA, 38 + rng() * 24, 56 + rng() * 12);
    const warm = hslToRgb(hueB, 36 + rng() * 26, 56 + rng() * 12);
    const bridgeHue = (hueA + hueB) / 2 + (rng() - 0.5) * 24;
    const bridge = hslToRgb(bridgeHue, 22 + rng() * 18, 76 + rng() * 8);

    return [cool, bridge, warm];
}

// ── Shaders ───────────────────────────────────────────────────────────────────

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

// Ashima simplex noise + domain-warped gradient field — same source as generator.
const FRAG = `
precision highp float;
uniform vec2  u_res;
uniform float u_time;
uniform vec3  u_c0;
uniform vec3  u_c1;
uniform vec3  u_c2;
uniform float u_seed;
uniform float u_warp;
uniform float u_speed;
uniform float u_grain;
uniform float u_scale;
uniform vec2  u_axis;

vec3 mod289v3(vec3 x){return x - floor(x*(1.0/289.0))*289.0;}
vec4 mod289v4(vec4 x){return x - floor(x*(1.0/289.0))*289.0;}
vec4 permute(vec4 x){return mod289v4(((x*34.0)+1.0)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}

float snoise(vec3 v){
  const vec2 C=vec2(1.0/6.0,1.0/3.0);
  const vec4 D=vec4(0.0,0.5,1.0,2.0);
  vec3 i=floor(v+dot(v,C.yyy));
  vec3 x0=v-i+dot(i,C.xxx);
  vec3 gg=step(x0.yzx,x0.xyz);
  vec3 l=1.0-gg;
  vec3 i1=min(gg.xyz,l.zxy);
  vec3 i2=max(gg.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx;
  vec3 x2=x0-i2+C.yyy;
  vec3 x3=x0-D.yyy;
  i=mod289v3(i);
  vec4 p=permute(permute(permute(
    i.z+vec4(0.0,i1.z,i2.z,1.0))
   +i.y+vec4(0.0,i1.y,i2.y,1.0))
   +i.x+vec4(0.0,i1.x,i2.x,1.0));
  float n_=0.142857142857;
  vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.0*floor(p*ns.z*ns.z);
  vec4 x_=floor(j*ns.z);
  vec4 y_=floor(j-7.0*x_);
  vec4 xv=x_*ns.x+ns.yyyy;
  vec4 yv=y_*ns.x+ns.yyyy;
  vec4 h=1.0-abs(xv)-abs(yv);
  vec4 b0=vec4(xv.xy,yv.xy);
  vec4 b1=vec4(xv.zw,yv.zw);
  vec4 s0=floor(b0)*2.0+1.0;
  vec4 s1=floor(b1)*2.0+1.0;
  vec4 sh=-step(h,vec4(0.0));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;
  vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x);
  vec3 p1=vec3(a0.zw,h.y);
  vec3 p2=vec3(a1.xy,h.z);
  vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x; p1*=norm.y; p2*=norm.z; p3*=norm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);
  m=m*m;
  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}

float hash12(vec2 p){
  vec3 p3=fract(vec3(p.xyx)*0.1031);
  p3+=dot(p3,p3.yzx+33.33);
  return fract((p3.x+p3.y)*p3.z);
}

void main(){
  vec2 uv=(gl_FragCoord.xy-0.5*u_res)/(0.5*min(u_res.x,u_res.y));
  float r=length(uv);
  float aa=2.0/min(u_res.x,u_res.y);
  float mask=1.0-smoothstep(1.0-aa*1.5,1.0,r);
  if(mask<=0.0){gl_FragColor=vec4(0.0);return;}
  float t=u_time*u_speed;
  vec2 p=uv*u_scale+u_seed;
  vec2 w1=vec2(
    snoise(vec3(p*0.7,       t*0.25)),
    snoise(vec3(p*0.7+17.7,  t*0.30))
  );
  vec2 s=p+u_warp*w1;
  float field=snoise(vec3(s*0.55,t*0.12));
  float gv=dot(uv,u_axis)*0.5+0.5;
  float mv=clamp(gv+0.45*field,0.0,1.0);
  vec3 col=mix(u_c0,u_c1,smoothstep(0.20,0.52,mv));
  col=mix(col,u_c2,smoothstep(0.50,0.80,mv));
  col*=1.0-0.08*smoothstep(0.6,1.0,r);
  float gr=hash12(gl_FragCoord.xy+fract(t*7.0)*311.0)-0.5;
  col+=gr*u_grain;
  gl_FragColor=vec4(col*mask,mask);
}
`;

// ── WebGL renderer factory ────────────────────────────────────────────────────

interface OrbParams {
    colors: [RGB, RGB, RGB];
    noiseSeed: number;
    axis: [number, number];
    speed: number;
    warp: number;
    grain: number;
    scale: number;
    /** CSS rgba string for the halo glow, derived from the warm palette anchor. */
    haloColor: string;
}

type RenderFn = (params: OrbParams, time: number, w: number, h: number) => void;

function createOrbRenderer(canvas: HTMLCanvasElement): RenderFn | null {
    const gl = canvas.getContext('webgl', {
        alpha: true,
        antialias: true,
        premultipliedAlpha: true,
        preserveDrawingBuffer: false,
    });
    if (!gl) return null;

    const vs = gl.createShader(gl.VERTEX_SHADER)!;
    gl.shaderSource(vs, VERT);
    gl.compileShader(vs);

    const fs = gl.createShader(gl.FRAGMENT_SHADER)!;
    gl.shaderSource(fs, FRAG);
    gl.compileShader(fs);

    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    // Uniform locations — names are GLSL identifiers (external shader protocol).
    const uRes = gl.getUniformLocation(prog, 'u_res') as WebGLUniformLocation;
    const uTime = gl.getUniformLocation(prog, 'u_time') as WebGLUniformLocation;
    const uC0 = gl.getUniformLocation(prog, 'u_c0') as WebGLUniformLocation;
    const uC1 = gl.getUniformLocation(prog, 'u_c1') as WebGLUniformLocation;
    const uC2 = gl.getUniformLocation(prog, 'u_c2') as WebGLUniformLocation;
    const uSeed = gl.getUniformLocation(prog, 'u_seed') as WebGLUniformLocation;
    const uWarp = gl.getUniformLocation(prog, 'u_warp') as WebGLUniformLocation;
    const uSpeed = gl.getUniformLocation(prog, 'u_speed') as WebGLUniformLocation;
    const uGrain = gl.getUniformLocation(prog, 'u_grain') as WebGLUniformLocation;
    const uScale = gl.getUniformLocation(prog, 'u_scale') as WebGLUniformLocation;
    const uAxis = gl.getUniformLocation(prog, 'u_axis') as WebGLUniformLocation;

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    return function render(params: OrbParams, time: number, w: number, h: number) {
        if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w;
            canvas.height = h;
        }
        gl.viewport(0, 0, w, h);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.uniform2f(uRes, w, h);
        gl.uniform1f(uTime, time);
        gl.uniform3fv(uC0, params.colors[0]);
        gl.uniform3fv(uC1, params.colors[1]);
        gl.uniform3fv(uC2, params.colors[2]);
        gl.uniform1f(uSeed, params.noiseSeed);
        gl.uniform1f(uWarp, params.warp);
        gl.uniform1f(uSpeed, params.speed);
        gl.uniform1f(uGrain, params.grain);
        gl.uniform1f(uScale, params.scale);
        gl.uniform2f(uAxis, params.axis[0], params.axis[1]);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
}

// ── Orb params ────────────────────────────────────────────────────────────────

function buildOrbParams(seed: number): OrbParams {
    const rng = mulberry32(seed);
    const colors = makePalette(rng);
    const warm = colors[2];
    const angle = rng() * Math.PI * 2;

    return {
        colors,
        noiseSeed: rng() * 100,
        axis: [Math.cos(angle), Math.sin(angle)],
        speed: 0.3 + rng() * 0.7,
        warp: 0.45 + rng() * 0.65,
        grain: 0.015 + rng() * 0.03,
        scale: 0.55 + rng() * 0.55,
        // Halo color matches the HTML: warm anchor at 16% opacity.
        haloColor: `rgba(${Math.round(warm[0] * 255)},${Math.round(warm[1] * 255)},${Math.round(warm[2] * 255)},0.16)`,
    };
}

// ── React component ───────────────────────────────────────────────────────────

interface OrbAvatarProps {
    /** Display size in CSS pixels. Canvas backing store scales with devicePixelRatio. */
    size?: number;
    /** RNG seed for palette + animation character. Defaults to the agent's fixed identity. */
    seed?: number;
    className?: string;
}

const OrbAvatar = ({ size = 32, seed = AGENT_SEED, className }: OrbAvatarProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const params = useMemo(() => buildOrbParams(seed), [seed]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const render = createOrbRenderer(canvas);
        if (!render) return; // WebGL unavailable — canvas stays transparent

        let rafId: number;

        // Arrow function (not hoisted) keeps TypeScript's null-narrowing of `render` intact.
        const frame = (ms: number) => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const px = Math.round(size * dpr);
            render(params, ms * 0.001, px, px);
            rafId = requestAnimationFrame(frame);
        };

        rafId = requestAnimationFrame(frame);

        return () => cancelAnimationFrame(rafId);
    }, [params, size]);

    return (
        <div className={className} style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            {/* Halo glow behind the orb — explicit px offset avoids % rounding on non-square layouts */}
            <div
                aria-hidden
                style={{
                    position: 'absolute',
                    top: -Math.round(size * 0.12),
                    left: -Math.round(size * 0.12),
                    width: Math.round(size * 1.24),
                    height: Math.round(size * 1.24),
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${params.haloColor} 0%, transparent 62%)`,
                    filter: 'blur(20px)',
                    zIndex: -1,
                    pointerEvents: 'none',
                }}
            />

            {/* WebGL canvas — border-radius clips it to a circle */}
            <canvas
                ref={canvasRef}
                width={size}
                height={size}
                aria-hidden
                style={{
                    width: size,
                    height: size,
                    display: 'block',
                    borderRadius: '50%',
                }}
            />
        </div>
    );
};

export default OrbAvatar;
